import { assign, createMachine } from "xstate";
import { createOrchestrator } from "@nju/data-access-portal-orchestrator";
import { IFetchOrderProductsResponse } from "@nju/data-access-portal-product";
import { ISelectedSimCardType } from "../../types";

interface IContext {
  userId: string;
  dataCode: string;
  errorCode?: string;
  selectedSimCardType: ISelectedSimCardType;
  products: IFetchOrderProductsResponse;
}

type IEvent =
  | { type: "SET_CODE"; value: string | undefined }
  | { type: "SUCCESS" }
  | { type: "ERROR" }
  | { type: "WRONG_DATA" }
  | { type: "SUBMIT" };

export const changeSimEsimDataVerificationMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "init",
    states: {
      init: {
        on: {
          SET_CODE: {
            actions: "setDataCode",
          },
          SUBMIT: {
            target: "validate",
          },
        },
      },
      validate: {
        entry: "validateForm",
        always: [
          {
            cond: "hasNoDataCodeErrors",
            target: "setExchangeSim",
          },
          {
            target: "init",
          },
        ],
      },
      setExchangeSim: {
        invoke: {
          src: "setExchangeSim",
        },
        on: {
          SUCCESS: {
            actions: "redirectToChangeSimSummaryPage",
            target: "final",
          },
          WRONG_DATA: {
            target: "wrongData",
          },
          ERROR: {
            target: "error",
          },
        },
      },
      final: {
        type: "final",
      },
      wrongData: {
        type: "final",
      },
      error: {
        type: "final",
      },
    },
  },
  {
    guards: {
      hasNoDataCodeErrors: ({ errorCode }) => {
        return errorCode === undefined;
      },
    },
    actions: {
      setDataCode: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_CODE") {
          return {
            dataCode: event.value,
          };
        }

        return context;
      }),
      validateForm: assign<IContext, IEvent>({
        errorCode: ({ dataCode }) => {
          if (!dataCode) {
            return "Wpisz 4 ostatnie znaki.";
          }
          if (dataCode.length < 4) {
            return "To pole musi zawierać 4 znaki.";
          }
          return;
        },
      }),
    },
    services: {
      setExchangeSim: ({ userId, dataCode, selectedSimCardType, products }) => {
        return async (callback) => {
          try {
            const delivery = JSON.parse(
              window.localStorage.getItem("sim_delivery_address") as string,
            );

            const result = await createOrchestrator("NJU_SIM_MANAGEMENT", {
              offerIds: ["P-NJU-SIMCard"],
              processId: "EXCHANGE_SIM",
              deleteProductId: products.mobileProducts.simCard?.id,
              product: { id: products.mobileProducts.mobileAccess?.id },
              userId,
              deliveryMethodId:
                selectedSimCardType === "eSIM" ? "ESIM" : "COURIER",
              confirmationCode: dataCode,
              ...(selectedSimCardType === "eSIM" && { esimType: "ESIMNOPIN" }),
              ...(selectedSimCardType === "SIM" && { delivery }),
            });

            if (result.isError) {
              if (result.err.code === "WRONG_DATA") {
                return callback({ type: "WRONG_DATA" });
              }

              return callback({ type: "ERROR" });
            }

            window.localStorage.removeItem("sim_delivery_address");

            return callback({
              type: "SUCCESS",
            });
          } catch {
            return callback({ type: "ERROR" });
          }
        };
      },
    },
  },
);
